import type { Row } from "@rocicorp/zero";
import { styled } from "goober";
import { useEffect, useState } from "react";
import { queries } from "../../shared/queries";
import { markAdminViewSpace } from "../hooks/useAdminSpaceView";
import { useCurrentUserId } from "../hooks/useCurrentUserId";
import { useHasAccess } from "../hooks/useHasAccess";
import { useIsMobile } from "../hooks/useIsMobile";
import { useNavTransition } from "../hooks/useNavTransition";
import { usePersonalWorkEnabled } from "../hooks/usePersonalWorkEnabled";
import { useQuery } from "@rocicorp/zero/react";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../hooks/useSpaceDisplayName";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { spaceIcon } from "../lib/spaceIcon";
import { useMakeUrl, routes, useRouteMatch } from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import { Avatar } from "./Avatar";
import { SearchableDropdown, RowButton } from "./SearchableDropdown";
import { DiscoverableSpaceIcon } from "./DiscoverableSpaceIcon";
import { Icon } from "./Icon";
import {
  Break,
  Container,
  Heading,
  SpaceItemContainer,
  StyledLink,
} from "./Navigation";
import { SpaceActionsMenu } from "./SpaceActionsMenu";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

type Member = Row["user"];

const PickerRow = styled("div")`
  flex: 1;
  min-width: 0;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  color: var(--text-primary);

  &:hover {
    background: var(--alpha4);
  }
`;

function MemberPicker({
  chosen,
  onChoose,
}: {
  chosen: Member | undefined;
  onChoose: (userId: string) => void;
}) {
  const currentUserId = useCurrentUserId();
  const [allUsers] = useQuery(queries.allUsers());
  const members = allUsers
    .filter((u) => u.id !== currentUserId)
    .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));

  return (
    <SearchableDropdown
      align="left"
      searchPlaceholder="Search members…"
      opener={
        <PickerRow>
          {chosen ? (
            <Avatar
              user={chosen}
              size={16}
              tooltip={false}
              isMuted={chosen.deactivated === true}
            />
          ) : (
            <Icon name="circle-user-round" size={16} />
          )}
          <span style={{ flex: 1, minWidth: 0 }}>
            <Text ellipsis>
              {chosen ? userDisplayName(chosen) : "Select member…"}
            </Text>
          </span>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerRow>
      }
    >
      {(query, close) => {
        const q = query.toLowerCase();
        const matches = members.filter(
          (u) =>
            userDisplayName(u).toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q)
        );
        return matches.map((u) => (
          <RowButton
            key={u.id}
            onClick={() => {
              onChoose(u.id);
              close();
            }}
          >
            <Avatar
              user={u}
              size={20}
              tooltip={false}
              isMuted={u.deactivated === true}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text variant="text-regular" color="var(--text-primary)" ellipsis>
                {userDisplayName(u)}
              </Text>
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                {u.email}
              </Text>
            </div>
          </RowButton>
        ));
      }}
    </SearchableDropdown>
  );
}

function MemberLink({
  href,
  active,
  icon,
  label,
}: {
  href: string;
  active: boolean;
  icon: "circle-user-round" | "ask";
  label: string;
}) {
  return (
    <SpaceItemContainer>
      <StyledLink
        to={href}
        style={{
          backgroundColor: active ? "var(--alpha4)" : "transparent",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <Icon name={icon} size={16} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <Text ellipsis>{label}</Text>
        </span>
      </StyledLink>
    </SpaceItemContainer>
  );
}

function SpaceLink({
  space,
  notMember,
}: {
  space: DisplayableSpace & { slug?: string | null };
  notMember: boolean;
}) {
  const makeUrl = useMakeUrl();
  const displayName = useSpaceDisplayName();
  const isMobile = useIsMobile();
  const [active] = useRouteMatch(routes.space, { spaceId: space.id });
  const [hover, setHover] = useState(false);

  // Runs ahead of Link's own handlers, and only for activations that navigate
  // this tab — a marked space that never gets visited would resurrect the
  // admin view on a later ordinary visit.
  const markForNavigation = (event: React.MouseEvent) => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey) return;
    markAdminViewSpace(space.id);
  };

  return (
    <SpaceItemContainer
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <StyledLink
        to={makeUrl(routes.space, { spaceId: space.id })}
        onMouseDown={markForNavigation}
        onClick={markForNavigation}
        style={{
          backgroundColor: active || hover ? "var(--alpha4)" : "transparent",
          flex: 1,
          overflow: "hidden",
          paddingRight: 32,
        }}
      >
        {notMember ? (
          <Tooltip content="Not a member">
            <DiscoverableSpaceIcon />
          </Tooltip>
        ) : (
          <Icon name={spaceIcon(space.mode)} size={16} />
        )}
        <span style={{ flex: 1, minWidth: 0 }}>
          <Text ellipsis>{displayName(space)}</Text>
        </span>
      </StyledLink>
      <div
        data-space-action
        style={{
          position: "absolute",
          right: 4,
          opacity: isMobile ? 1 : undefined,
          pointerEvents: isMobile ? "auto" : undefined,
        }}
      >
        <SpaceActionsMenu space={space} />
      </div>
    </SpaceItemContainer>
  );
}

let lastChosenMemberId: string | null = null;

export function AdminNavigation({
  subjectUserId,
}: {
  subjectUserId: string | null;
}) {
  const makeUrl = useMakeUrl();
  const navEnter = useNavTransition("admin");
  const canReadSpaces = useHasAccess("tenant:spaces_read");
  const canReadChats = useHasAccess("tenant:personal_chats_read");
  const personalWorkEnabled = usePersonalWorkEnabled();
  const [allUsers] = useQuery(queries.allUsers());
  const [personalOwners] = useQuery(queries.personalSpaceOwners());
  const [spaces] = useSpaces({ settingsView: true });

  const [chosenId, setChosenId] = useState(subjectUserId ?? lastChosenMemberId);
  const [prevSubjectUserId, setPrevSubjectUserId] = useState(subjectUserId);
  if (subjectUserId !== prevSubjectUserId) {
    setPrevSubjectUserId(subjectUserId);
    if (subjectUserId) setChosenId(subjectUserId);
  }
  // Remember the latest selection across remounts (seeds the initial state).
  useEffect(() => {
    if (subjectUserId) lastChosenMemberId = subjectUserId;
  }, [subjectUserId]);
  const choose = (userId: string) => {
    lastChosenMemberId = userId;
    setChosenId(userId);
  };

  const chosen = chosenId
    ? allUsers?.find((u) => u.id === chosenId)
    : undefined;
  const personalSpace = chosenId
    ? personalOwners?.find((s) => s.roleGrantsAsScope[0]?.user?.id === chosenId)
    : undefined;

  const [, spaceMatch] = useRouteMatch(routes.space);
  const personalActive =
    !!personalSpace &&
    (spaceMatch as { spaceId?: string }).spaceId === personalSpace.id;
  const [onChats, chatsMatch] = useRouteMatch(routes.chats);
  const chatsActive =
    !!onChats && (chatsMatch as { userId?: string }).userId === chosenId;

  const memberSpaceIds = new Set(
    spaces.filter(isSpaceMember).map((space) => space.id)
  );
  const tenantSpaces = spaces.filter((space) => space.mode !== "personal");

  const name = chosen ? userDisplayName(chosen) : null;

  return (
    <Container data-nav-enter={navEnter}>
      {personalWorkEnabled && (
        <>
          <Heading>
            <Text variant="text-small">Personal and chat</Text>
          </Heading>
          <MemberPicker chosen={chosen} onChoose={choose} />

          {name && chosenId && canReadSpaces && personalSpace && (
            <MemberLink
              href={makeUrl(routes.space, { spaceId: personalSpace.id })}
              active={personalActive}
              icon="circle-user-round"
              label={`${name}’s personal workflows`}
            />
          )}

          {name && chosenId && canReadChats && (
            <MemberLink
              href={makeUrl(routes.chats, { userId: chosenId })}
              active={chatsActive}
              icon="ask"
              label={`${name}’s chats`}
            />
          )}
        </>
      )}

      {canReadSpaces && (
        <>
          {personalWorkEnabled && <Break />}
          <Heading>
            <Text variant="text-small">All spaces</Text>
          </Heading>
          {tenantSpaces.map((space) => (
            <SpaceLink
              key={space.id}
              space={space}
              notMember={!memberSpaceIds.has(space.id)}
            />
          ))}
        </>
      )}
    </Container>
  );
}
